/**
 * Where a member goes after signing in: back to the page they asked for —
 * with its query and hash, so a `/?guide=<key>` link still opens the guide
 * after login (#436). Only a path of this portal: never another origin
 * (`//host`, `/\host`, schemes), which would be an open redirect.
 */

/** A path the browser would read as another origin: `//host`, `/\host`. */
function protocolRelative(path: string): boolean {
  return path.startsWith("//") || path.startsWith("/\\");
}

/**
 * `path` if it is a same-origin relative path, else `/`. The FINAL path is
 * checked after the URL parser has normalised it: dot segments (`/..//host`,
 * `/%2e%2e//host`) collapse to `//host`, which would leave the portal.
 */
export function safeRedirect(path: string | null | undefined, origin: string = window.location.origin): string {
  // eslint-disable-next-line no-control-regex
  if (!path || !path.startsWith("/") || protocolRelative(path) || /[\\\s\x00-\x1f\x7f]/.test(path)) return "/";
  try {
    const base = new URL(origin);
    const url = new URL(path, base);
    if (url.origin !== base.origin) return "/";
    const out = `${url.pathname}${url.search}${url.hash}`;
    return out.startsWith("/") && !protocolRelative(out) ? out : "/";
  } catch {
    return "/";
  }
}

/** The page to come back to after login: path, query and hash, without the
 *  `?auth=` that only starts the sign-in. */
export function loginReturnPath(location: { pathname: string; search: string; hash: string }): string {
  const params = new URLSearchParams(location.search);
  params.delete("auth");
  const search = params.toString();
  return `${location.pathname}${search ? `?${search}` : ""}${location.hash}`;
}

/** localStorage key for the page a logged-out member was headed to, so the
 *  return path survives a cold load in a new tab (a Slack call link opens one)
 *  and a manual sign-in, where router history state does not. */
export const PENDING_LOGIN_RETURN_KEY = "nodus.portal.pendingLoginReturn";

/** Remember where a logged-out member was headed (the doorbell call page in a
 *  Slack link, `/?guide=`...). Cleared at logout with the login (a `#` target
 *  survives logout, a query target does not — the popup precedent), and read
 *  once by the login page. */
export function rememberLoginReturn(path: string): void {
  try {
    if (path && path !== "/") window.localStorage.setItem(PENDING_LOGIN_RETURN_KEY, path);
  } catch {
    // blocked or full storage: the return path lives in router state only
  }
}

/** The remembered return path, if any. */
export function readPendingLoginReturn(): string | null {
  try {
    return window.localStorage.getItem(PENDING_LOGIN_RETURN_KEY);
  } catch {
    return null;
  }
}

/** Forget the remembered return path (consumed by the login page). */
export function clearPendingLoginReturn(): void {
  try {
    window.localStorage.removeItem(PENDING_LOGIN_RETURN_KEY);
  } catch {
    // nothing to clear then
  }
}
